import { defineStore } from 'pinia';
import type { CmsArticleView, CmsAssetView, CmsChannelView, CmsColumnView } from 'shared';
import { adminApi } from '@/api';

export const useCmsContentStore = defineStore('cms-content', {
  state: () => ({
    loading: false,
    articles: [] as CmsArticleView[],
    columns: [] as CmsColumnView[],
    channels: [] as CmsChannelView[],
  }),
  actions: {
    async load(): Promise<void> {
      this.loading = true;
      try {
        const [articles, columns, channels] = await Promise.all([
          adminApi.get<CmsArticleView[]>('/admin/cms/articles'),
          adminApi.get<CmsColumnView[]>('/admin/cms/columns'),
          adminApi.get<CmsChannelView[]>('/admin/cms/channels'),
        ]);
        this.articles = articles;
        this.columns = columns;
        this.channels = channels;
      } finally {
        this.loading = false;
      }
    },
    async save(article: Partial<CmsArticleView> & { reason: string }): Promise<CmsArticleView> {
      const saved = article.id
        ? await adminApi.patch<CmsArticleView>(`/admin/cms/articles/${article.id}`, article)
        : await adminApi.post<CmsArticleView>('/admin/cms/articles', article);
      const index = this.articles.findIndex((item) => item.id === saved.id);
      if (index < 0) this.articles.unshift(saved);
      else this.articles.splice(index, 1, saved);
      return saved;
    },
    async saveColumn(column: Omit<CmsColumnView, 'id'> & { id?: string; reason: string }): Promise<CmsColumnView> {
      const saved = await adminApi.post<CmsColumnView>('/admin/cms/columns', column);
      const index = this.columns.findIndex((item) => item.id === saved.id);
      if (index < 0) this.columns.push(saved);
      else this.columns.splice(index, 1, saved);
      this.columns.sort((left, right) => left.channelKey.localeCompare(right.channelKey) || left.sort - right.sort);
      return saved;
    },
    async publish(id: string, version: number, reason: string, scheduledAt?: string): Promise<CmsArticleView> {
      const saved = await adminApi.post<CmsArticleView>(`/admin/cms/articles/${id}/publish`, { version, reason, scheduledAt: scheduledAt || null });
      const index = this.articles.findIndex((item) => item.id === id);
      if (index >= 0) this.articles.splice(index, 1, saved);
      return saved;
    },
    async uploadAsset(file: File): Promise<CmsAssetView> {
      return adminApi.upload<CmsAssetView>('/admin/cms/assets', file);
    },
  },
});
